import Seo from "../components/Seo";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileCallBar from "../components/MobileCallBar";

export default function Terms() {
    return (
        <div className="bg-white font-sans text-navy">
            <Seo
                title="Terms and Conditions | Hexagon EOR"
                description="The terms that govern use of the Hexagon EOR website and the information and services described on it."
                path="/terms-and-conditions"
            />
            <Header />
            <main className="mx-auto max-w-3xl px-5 pb-24 pt-36 sm:px-8 lg:pt-40">
                <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-royal">
                    <span className="h-px w-10 bg-gold" aria-hidden="true" />
                    Legal
                </p>
                <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tighter text-navy sm:text-5xl">
                    Terms and Conditions
                </h1>
                <p className="mt-4 text-sm text-navy/50">
                    Last updated: July 2026
                </p>

                <div className="prose-legal mt-10" data-testid="terms-content">
                    <h2>1. About These Terms</h2>
                    <p>
                        These Terms and Conditions ("Terms") govern your use of
                        the Hexagon EOR website at hexagoneor.com (the
                        "Website"), operated by Hexagon EOR ("Hexagon", "we",
                        "us", or "our"). By accessing or using the Website, you
                        agree to these Terms. If you do not agree, please do
                        not use the Website.
                    </p>

                    <h2>2. Purpose of the Website</h2>
                    <p>
                        The Website provides general information about Hexagon
                        EOR's employer of record, international employment,
                        workforce administration, and employee onboarding
                        services, and offers ways to contact our team by phone
                        or email. Content on the Website is provided for
                        general informational purposes only.
                    </p>

                    <h2>3. Service Descriptions and Availability</h2>
                    <p>
                        Descriptions of services on the Website are summaries
                        and do not constitute an offer capable of acceptance or
                        a binding agreement. Services are available only in
                        supported markets, which primarily include the United
                        States with additional availability in Canada, and
                        availability for any specific situation is confirmed
                        during consultation. Any engagement for services is
                        subject to a separately negotiated and executed written
                        service agreement, which governs the actual scope,
                        fees, responsibilities, and terms of the services.
                        Nothing on the Website replaces or overrides a signed
                        service agreement or any applicable law.
                    </p>

                    <h2>4. Website Information Is Not Professional Advice</h2>
                    <p>
                        Hexagon EOR provides legal and tax services as part of
                        its professional offerings. However, the content of the
                        Website is general information only: it does not
                        constitute legal, tax, or other professional advice, and
                        accessing or using the Website does not create a
                        professional-client relationship. Professional services
                        are provided only under a separately executed engagement
                        or service agreement, and you should consult a qualified
                        professional about your specific circumstances before
                        acting on any information found on the Website.
                    </p>

                    <h2>5. Acceptable Use</h2>
                    <p>You agree not to use the Website to:</p>
                    <ul>
                        <li>Violate any applicable law or regulation</li>
                        <li>Attempt to gain unauthorized access to the Website, its servers, or related systems</li>
                        <li>Interfere with or disrupt the Website's operation or security</li>
                        <li>Scrape, harvest, or collect information from the Website by automated means without our permission</li>
                        <li>Impersonate any person or business, or misrepresent your affiliation</li>
                    </ul>

                    <h2>6. Intellectual Property</h2>
                    <p>
                        The Website and its content — including text, design,
                        graphics, logos, and the Hexagon name and mark — are
                        owned by or licensed to Hexagon EOR and are protected
                        by intellectual property laws. You may view and share
                        pages of the Website for personal or internal business
                        evaluation purposes, but you may not reproduce,
                        distribute, modify, or create derivative works from
                        Website content without our prior written consent.
                    </p>

                    <h2>7. Third-Party Links and Services</h2>
                    <p>
                        The Website may contain links to third-party websites
                        or may rely on third-party services such as hosting and
                        analytics providers. We do not control and are not
                        responsible for the content, policies, or practices of
                        third-party websites or services. Following a link to a
                        third-party site is at your own discretion and subject
                        to that site's own terms and policies.
                    </p>

                    <h2>8. Disclaimers</h2>
                    <p>
                        The Website is provided on an "as is" and "as
                        available" basis. To the fullest extent permitted by
                        law, we disclaim all warranties, express or implied,
                        regarding the Website, including warranties of accuracy,
                        completeness, merchantability, fitness for a particular
                        purpose, and non-infringement. We do not warrant that
                        the Website will be uninterrupted, error-free, or free
                        of harmful components.
                    </p>

                    <h2>9. Limitation of Liability</h2>
                    <p>
                        To the fullest extent permitted by applicable law,
                        Hexagon EOR and its personnel will not be liable for
                        any indirect, incidental, consequential, special, or
                        punitive damages, or for any loss of profits, revenue,
                        data, or goodwill, arising out of or related to your
                        use of or inability to use the Website. This limitation
                        applies to Website use only; liability in connection
                        with any actual services is governed exclusively by the
                        applicable signed service agreement. Nothing in these
                        Terms excludes liability that cannot be excluded under
                        applicable law.
                    </p>

                    <h2>10. Suspension or Restriction of Access</h2>
                    <p>
                        We may suspend, restrict, or terminate access to the
                        Website at any time, without notice, where we reasonably
                        believe use of the Website violates these Terms,
                        applicable law, or poses a security or operational risk.
                    </p>

                    <h2>11. Changes to the Website or These Terms</h2>
                    <p>
                        We may update the Website and these Terms from time to
                        time. The current version of the Terms will always be
                        posted on this page with a revised "Last updated" date.
                        Continued use of the Website after changes are posted
                        constitutes acceptance of the updated Terms.
                    </p>

                    <h2>12. Governing Law and Disputes</h2>
                    <p>
                        These Terms and any dispute arising from your use of
                        the Website are governed by the laws of the
                        jurisdiction in which Hexagon EOR is organized, without
                        regard to conflict-of-law principles, and the courts of
                        that jurisdiction will have jurisdiction over such
                        disputes, except where applicable law provides
                        otherwise. Disputes relating to any actual services are
                        governed by the dispute provisions of the applicable
                        signed service agreement.
                    </p>

                    <h2>13. Contact</h2>
                    <p>
                        If you have questions about these Terms, contact us at{" "}
                        <a href="mailto:info@hexagoneor.com" data-testid="terms-email-link">
                            info@hexagoneor.com
                        </a>{" "}
                        or by phone at{" "}
                        <a href="tel:+18333439367" data-testid="terms-phone-link">
                            +1 833-343-9367
                        </a>
                        .
                    </p>
                </div>
            </main>
            <Footer />
            <div className="h-[68px] md:hidden" aria-hidden="true" />
            <MobileCallBar />
        </div>
    );
}
